import * as zip from "@zip.js/zip.js";
import {Index} from 'flexsearch';
import { sng2json, type Song } from './songbeamer';
import AES from 'crypto-js/aes';
import { decrypt } from "./crypt";

export interface SongWFile extends Song {
	file: string;
}

function getTextsAsStr(song: Song){
	const cnt = song.props.LangCount || 1;
	const texts: string[] = Array(cnt).fill('');
	for(let l = 0; l < cnt; l++){
		let title = song.props.Title;
		if((song.props as any)[`TitleLang${l}`]){
			title = (song.props as any)[`TitleLang${l}`];
		}
		texts[l] += `${title}\n`;
		if(song.props.Author){
			texts[l] += `${song.props.Author}\n`;
		}
		if(song.props.Songbook){
			texts[l] += `${song.props.Songbook}\n`;
		}
		for(const verse in song.texts){
			for(const slide of song.texts[verse]){
				for(const line of slide){
					texts[l] += line + '\n';
				}
			}
		}
	}
	return texts;
}

async function initIndex(songs: Map<string, SongWFile>, etag: string){
	const index = new Index({
		tokenize: 'forward'
	});
	for(const song of Array.from(songs.values())){
		const texts = getTextsAsStr(song);
		for(let i = 0; i < texts.length; i++){
			await index.addAsync(song.file + '$' + i, texts[i]);
		}
	}
	return index;
}

export async function initSongDb(): Promise<{songs: Map<string, SongWFile>, index: Index}>{
	const req = await fetch('songs.zip.enc');
	if(!req.ok){
		throw new Error(`Failed to load songs: ${req.status} ${req.statusText}`);
	}
	if(req.headers.has('ETag')){
		const storedJson = localStorage.getItem('songs');
		if(storedJson){
			const stored = JSON.parse(storedJson);
			if(stored.etag === req.headers.get('ETag')){
				const songs = new Map<string, SongWFile>();
				for(const song of stored.songs){
					songs.set(song.file, song);
				}
				return {songs, index: await initIndex(songs, stored.etag)};
			}
		}
	}
	const arrayBufferEnc = await req.arrayBuffer();
	let pw = localStorage.getItem('songsearch_pw');
	if(!pw){
		pw = prompt('Enter the password to decrypt the songs:');
		if(!pw){
			throw new Error('No password provided');
		}
		localStorage.setItem('songsearch_pw', pw);
	}
	const arrayBufferDec = await decrypt(arrayBufferEnc, pw);
	const zipReader = new zip.ZipReader(new zip.BlobReader(new Blob([arrayBufferDec])));
	const entries = await zipReader.getEntries();
	const songs = new Map<string, SongWFile>();
	for (const entry of entries) {
		if (entry.directory || !entry.filename.endsWith('.sng')) {
			continue;
		}
		if(!entry.filename.startsWith('Glaubenslieder/')){
			continue;
		}
		const fileData = await entry.getData(new zip.TextWriter());
		const song = sng2json(fileData);
		songs.set(entry.filename, {...song, file: entry.filename});
	}
	if(req.headers.has('ETag')){
		localStorage.setItem('songs', JSON.stringify({etag: req.headers.get('ETag'), songs: Array.from(songs.values())}));
	}
	return {songs, index: await initIndex(songs, req.headers.get('ETag') || '')};
}

export type SongDB = Awaited<ReturnType<typeof initSongDb>>;
